import { memo, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import InputAdornment from '@mui/material/InputAdornment';
import TextField, { TextFieldProps } from '@mui/material/TextField';

import { fDate } from 'src/utils/format-time';

import Iconify from 'src/components/iconify';
import CustomDateRangePicker, { shortDateLabel } from 'src/components/custom-date-range-picker';

// ----------------------------------------------------------------------

type Props = Omit<TextFieldProps, 'name' | 'value' | 'defaultValue' | 'onChange'> & {
  startName: string;
  endName: string;
  dialogTitle?: string;
  disablePast?: boolean;
};

function asDate(value: unknown): Date | null {
  if (!value) return null;

  const date = new Date(value as Date | string | number);

  return Number.isNaN(date.getTime()) ? null : date;
}

function dateRangeLabel(startDate: Date | null, endDate: Date | null) {
  if (startDate && endDate) return shortDateLabel(startDate, endDate);
  if (startDate) return `A partir de ${fDate(startDate, 'dd/MM/yyyy')}`;
  if (endDate) return `Até ${fDate(endDate, 'dd/MM/yyyy')}`;

  return '';
}

function RHFDateRangePicker({
  startName,
  endName,
  label = 'Validade',
  placeholder = 'Selecione o período',
  dialogTitle = 'Selecione a validade do cupom',
  disablePast = false,
  disabled,
  helperText,
  InputProps,
  inputProps,
  onClick,
  onKeyDown,
  ...other
}: Props) {
  const { control, trigger } = useFormContext();
  const { field: startField, fieldState: startFieldState } = useController({
    name: startName,
    control,
  });
  const { field: endField, fieldState: endFieldState } = useController({
    name: endName,
    control,
  });

  const [open, setOpen] = useState(false);
  const [draftStartDate, setDraftStartDate] = useState<Date | null>(null);
  const [draftEndDate, setDraftEndDate] = useState<Date | null>(null);

  const startDate = asDate(startField.value);
  const endDate = asDate(endField.value);
  const errorMessage = startFieldState.error?.message || endFieldState.error?.message;
  const draftError = Boolean(
    draftStartDate && draftEndDate && draftStartDate.getTime() > draftEndDate.getTime()
  );

  const handleOpen = () => {
    if (disabled) return;

    setDraftStartDate(startDate);
    setDraftEndDate(endDate);
    setOpen(true);
  };

  const handleChangeStartDate = (newValue: Date | null) => {
    setDraftStartDate(newValue);

    if (newValue && draftEndDate && newValue.getTime() > draftEndDate.getTime()) {
      setDraftEndDate(null);
    }
  };

  const handleApply = async () => {
    startField.onChange(draftStartDate);
    endField.onChange(draftEndDate);
    startField.onBlur();
    endField.onBlur();
    setOpen(false);
    await trigger([startName, endName]);
  };

  return (
    <>
      <TextField
        {...other}
        fullWidth
        label={label}
        placeholder={placeholder}
        value={dateRangeLabel(startDate, endDate)}
        disabled={disabled}
        inputRef={startField.ref}
        error={Boolean(errorMessage)}
        helperText={errorMessage || helperText}
        onClick={(event) => {
          onClick?.(event);
          if (!event.defaultPrevented) handleOpen();
        }}
        onKeyDown={(event) => {
          onKeyDown?.(event);
          if (!event.defaultPrevented && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            handleOpen();
          }
        }}
        inputProps={{
          ...inputProps,
          readOnly: true,
          role: 'button',
          'aria-haspopup': 'dialog',
          'aria-expanded': open,
        }}
        InputProps={{
          ...InputProps,
          readOnly: true,
          endAdornment: InputProps?.endAdornment ?? (
            <InputAdornment position="end">
              <Iconify icon="solar:calendar-date-bold" />
            </InputAdornment>
          ),
        }}
      />

      <CustomDateRangePicker
        variant="calendar"
        title={dialogTitle}
        startDate={draftStartDate}
        endDate={draftEndDate}
        onChangeStartDate={handleChangeStartDate}
        onChangeEndDate={setDraftEndDate}
        open={open}
        onClose={() => setOpen(false)}
        onApply={handleApply}
        onReset={() => {
          setDraftStartDate(null);
          setDraftEndDate(null);
        }}
        selected={Boolean(draftStartDate || draftEndDate)}
        error={draftError}
        disablePast={disablePast}
        startLabel="Data inicial"
        endLabel="Data final"
        resetLabel="Limpar"
        cancelLabel="Cancelar"
        applyLabel="Aplicar"
        errorMessage="A data final deve ser igual ou posterior à data inicial"
      />
    </>
  );
}

export default memo(RHFDateRangePicker);
